import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useContextMenuGuard } from '../../hooks/useContextMenuGuard';
import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger, ContextMenuLabel,
} from '../ui/context-menu';
import { Button } from '../ui/button';
import { Check, Spline, PlusCircle, Trash2, CornerDownRight, Waves, Minus, Unlink, Link2, LogOut } from 'lucide-react';
import {
  pathToD, segmentCount, segmentEnds, isCurved, closestT, defaultHandles, minNodes, nodeCount, nearestSegment,
  moveNode, setHandle, setNodeType, insertNode, deleteNode, setSegmentStraight, setSegmentCurved, setPathClosed, normalizeFreeform,
} from '../../lib/freeform';

const NODE = 7; // px, screen space
const HANDLE = 7;
const PRIMARY = 'hsl(262 83% 58%)';
const NODE_FILL = '#111827';

/**
 * On-canvas Publisher "Edit Points" editor for a freeform shape.
 * Rendered inside the page container (page px × zoom); node coordinates are the shape's 100×100 viewBox space.
 *
 *  • drag a point                  → move it (its control handles follow)
 *  • click a point                 → select it and show its two control handles (drag them; the node type rules apply)
 *  • Ctrl+click a segment          → add a point there and start dragging it
 *  • Ctrl+click a point            → delete it (min. 3 for closed / 2 for open paths)
 *  • right-click point / segment   → Delete/Add Point, Corner/Smooth/Straight, Straight/Curved Segment, Open/Close Path, Exit
 *
 * Every gesture is one undo step (PUSH_HISTORY at pointer-down, rawDispatch while dragging). The selection frame
 * is re-normalised live so it keeps hugging the geometry like Publisher.
 */
export const EditPointsEditor = ({ object, zoom, canvasRef }) => {
  const { dispatch, rawDispatch } = useEditor();
  const guard = useContextMenuGuard();
  const objRef = useRef(object);
  objRef.current = object;
  const ff = object.properties?.freeform;
  const [sel, setSel] = useState(null); // { pi, ni }
  const [hit, setHit] = useState(null); // context-menu target
  const drag = useRef(null);

  const w = Math.max(1, object.width), h = Math.max(1, object.height);
  const cx = (object.x + w / 2) * zoom, cy = (object.y + h / 2) * zoom;
  const rot = object.rotation || 0;
  const toLocal = useCallback((q) => [((q.x / 100) - 0.5) * w * zoom, ((q.y / 100) - 0.5) * h * zoom], [w, h, zoom]);

  /** Pointer → viewBox coordinates of the *current* frame (the frame moves while normalising). */
  const toView = useCallback((e) => {
    const o = objRef.current;
    const ow = Math.max(1, o.width), oh = Math.max(1, o.height);
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / zoom, py = (e.clientY - rect.top) / zoom;
    const dx = px - (o.x + ow / 2), dy = py - (o.y + oh / 2);
    const r = (-(o.rotation || 0) * Math.PI) / 180;
    const lx = dx * Math.cos(r) - dy * Math.sin(r), ly = dx * Math.sin(r) + dy * Math.cos(r);
    return { x: (lx / ow + 0.5) * 100, y: (ly / oh + 0.5) * 100 };
  }, [canvasRef, zoom]);

  /** Write a new freeform (re-normalising the frame). `history` = discrete edit → one explicit undo step. */
  const apply = useCallback((nextFf, history = true) => {
    const o = objRef.current;
    const norm = normalizeFreeform(o, nextFf);
    const data = norm
      ? { x: norm.x, y: norm.y, width: norm.width, height: norm.height, properties: { ...o.properties, freeform: norm.freeform } }
      : { properties: { ...o.properties, freeform: nextFf } };
    if (history) dispatch({ type: 'PUSH_HISTORY' });
    rawDispatch({ type: 'UPDATE_OBJECT', payload: { id: o.id, data } });
  }, [dispatch, rawDispatch]);

  /** Window-level drag (survives re-renders). kind: 'node' | 'in' | 'out' | 'segment' (moves both end points). */
  const startDrag = useCallback((kind, pi, ni, snapshot = true, e0 = null) => {
    if (snapshot) dispatch({ type: 'PUSH_HISTORY' });
    const last = e0 ? { x: e0.clientX, y: e0.clientY } : null;
    const onMove = (e) => {
      if (!drag.current) return;
      const cur = objRef.current.properties?.freeform;
      if (!cur || !cur.paths[pi] || !cur.paths[pi].nodes[ni]) return;
      if (kind === 'segment') {
        // translate by the pointer delta expressed in the *current* frame's viewBox units (the frame re-normalises live)
        if (!last) return;
        const o = objRef.current;
        const ow = Math.max(1, o.width), oh = Math.max(1, o.height);
        const dx = (e.clientX - last.x) / zoom, dy = (e.clientY - last.y) / zoom;
        last.x = e.clientX; last.y = e.clientY;
        const r = (-(o.rotation || 0) * Math.PI) / 180;
        const lx = dx * Math.cos(r) - dy * Math.sin(r), ly = dx * Math.sin(r) + dy * Math.cos(r);
        const vx = (lx / ow) * 100, vy = (ly / oh) * 100;
        const nj = (ni + 1) % cur.paths[pi].nodes.length;
        apply(moveNode(moveNode(cur, pi, ni, vx, vy), pi, nj, vx, vy), false);
        return;
      }
      const q = toView(e);
      if (kind === 'node') {
        const n = cur.paths[pi].nodes[ni];
        apply(moveNode(cur, pi, ni, q.x - n.x, q.y - n.y), false);
      } else apply(setHandle(cur, pi, ni, kind, q), false);
    };
    const stop = () => {
      drag.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    drag.current = { kind, pi, ni };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  }, [dispatch, toView, apply, zoom]);

  useEffect(() => () => { drag.current = null; }, []);
  // drop a stale selection when the node disappears (undo / delete)
  useEffect(() => {
    if (sel && !(ff?.paths[sel.pi]?.nodes[sel.ni])) setSel(null);
  }, [ff, sel]);

  const onNodeDown = (pi, ni) => (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) { removeNode(pi, ni); return; }
    setSel({ pi, ni });
    startDrag('node', pi, ni);
  };
  const onHandleDown = (pi, ni, which) => (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    startDrag(which, pi, ni);
  };
  const onSegDown = (pi, si) => (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    if (!(e.ctrlKey || e.metaKey)) {
      // plain drag on a segment moves that segment (both end points), like Office
      setSel(null);
      startDrag('segment', pi, si, true, e);
      return;
    }
    const path = ff.paths[pi];
    const [a, b] = segmentEnds(path, si);
    const q = toView(e);
    const t = closestT(a, b, q);
    dispatch({ type: 'PUSH_HISTORY' });
    const res = insertNode(ff, pi, si, t);
    apply(res.ff, false);
    setSel({ pi, ni: res.index });
    startDrag('node', pi, res.index, false);
  };

  const removeNode = (pi, ni) => {
    const path = ff.paths[pi];
    if (path.nodes.length <= minNodes(path)) return;
    apply(deleteNode(ff, pi, ni));
    setSel(null);
  };
  const addPointAt = (pi, si, q) => {
    const path = ff.paths[pi];
    const [a, b] = segmentEnds(path, si);
    const res = insertNode(ff, pi, si, closestT(a, b, q));
    apply(res.ff);
    setSel({ pi, ni: res.index });
  };
  const exit = () => rawDispatch({ type: 'SET_POINT_EDIT', payload: null });

  if (!ff || !ff.paths?.length) return null;

  const selNode = sel ? ff.paths[sel.pi]?.nodes[sel.ni] : null;
  const selHandles = sel && selNode ? defaultHandles(ff.paths[sel.pi], sel.ni) : null;
  const hitPath = hit ? ff.paths[hit.pi] : null;
  const hitNode = hit?.kind === 'node' && hitPath ? hitPath.nodes[hit.ni] : null;
  const hitSeg = hit?.kind === 'segment' && hitPath ? segmentEnds(hitPath, hit.si) : null;

  return (
    <ContextMenu onOpenChange={(open) => { guard.onOpenChange(open); if (!open) setHit(null); }}>
      <ContextMenuTrigger asChild>
        <svg className="absolute inset-0" style={{ width: '100%', height: '100%', overflow: 'visible', zIndex: 1000, pointerEvents: 'none' }} data-testid="edit-points-editor" aria-label="Shape points" role="group">
          <g transform={`translate(${cx} ${cy}) rotate(${rot})`}>
            {ff.paths.map((path, pi) => {
              const lp = path.nodes.map(toLocal);
              const local = { ...path, nodes: path.nodes.map((n) => ({ ...n, x: toLocal(n)[0], y: toLocal(n)[1], in: n.in ? { x: toLocal(n.in)[0], y: toLocal(n.in)[1] } : null, out: n.out ? { x: toLocal(n.out)[0], y: toLocal(n.out)[1] } : null })) };
              return (
                <g key={`p-${pi}`}>
                  <path d={pathToD(local)} fill="none" stroke={PRIMARY} strokeWidth="1" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" opacity="0.9" data-testid={`edit-path-${pi}`} />
                  {Array.from({ length: segmentCount(path) }, (_, si) => {
                    const [a, b] = segmentEnds(local, si);
                    const segD = `M${a.x},${a.y} ${isCurved(a, b) ? `C${(a.out || a).x},${(a.out || a).y} ${(b.in || b).x},${(b.in || b).y} ${b.x},${b.y}` : `L${b.x},${b.y}`}`;
                    return (
                      <path
                        key={`s-${si}`}
                        d={segD}
                        fill="none"
                        stroke="transparent"
                        strokeWidth="12"
                        style={{ pointerEvents: 'stroke', cursor: 'move' }}
                        onPointerDown={onSegDown(pi, si)}
                        onContextMenu={(e) => { const q = toView(e); setHit({ kind: 'segment', pi, si, at: q }); }}
                        data-testid={`edit-seg-${pi}-${si}`}
                      />
                    );
                  })}
                  {lp.map(([x, y], ni) => {
                    const selected = sel && sel.pi === pi && sel.ni === ni;
                    return (
                      <rect
                        key={`n-${ni}`}
                        x={x - NODE / 2}
                        y={y - NODE / 2}
                        width={NODE}
                        height={NODE}
                        fill={selected ? PRIMARY : NODE_FILL}
                        stroke="#ffffff"
                        strokeWidth="1.25"
                        style={{ pointerEvents: 'all', cursor: 'move' }}
                        onPointerDown={onNodeDown(pi, ni)}
                        onContextMenu={() => { setHit({ kind: 'node', pi, ni }); setSel({ pi, ni }); }}
                        data-testid={`edit-point-${pi}-${ni}`}
                        data-selected={selected ? 'true' : undefined}
                        data-point-type={path.nodes[ni].type}
                      />
                    );
                  })}
                </g>
              );
            })}
            {/* control handles of the selected point */}
            {selNode && selHandles && (() => {
              const [nx, ny] = toLocal(selNode);
              return ['in', 'out'].map((which) => {
                const c = selHandles[which];
                if (!c) return null;
                const [hx, hy] = toLocal(c);
                return (
                  <g key={which}>
                    <line x1={nx} y1={ny} x2={hx} y2={hy} stroke={PRIMARY} strokeWidth="1" vectorEffect="non-scaling-stroke" />
                    <rect x={hx - HANDLE / 2} y={hy - HANDLE / 2} width={HANDLE} height={HANDLE} fill="#ffffff" stroke={PRIMARY} strokeWidth="1.5" transform={`rotate(45 ${hx} ${hy})`} style={{ pointerEvents: 'all', cursor: 'crosshair' }} onPointerDown={onHandleDown(sel.pi, sel.ni, which)} data-testid={`edit-handle-${which}`} />
                  </g>
                );
              });
            })()}
          </g>
        </svg>
      </ContextMenuTrigger>
      <ContextMenuContent className="bg-popover border border-border shadow-md min-w-[220px]" style={guard.contentStyle} data-testid="edit-points-context-menu">
        {hit && hitPath && (
          <>
            <ContextMenuLabel className="text-[11px] text-muted-foreground">{hit.kind === 'node' ? `Point ${hit.ni + 1} of ${hitPath.nodes.length}` : `Segment ${hit.si + 1}`}</ContextMenuLabel>
            {/* Publisher lists both commands on every hit and greys out the one that does not apply */}
            <ContextMenuItem className="text-[13px] flex items-center gap-2" disabled={hit.kind !== 'segment'} onClick={() => hit.kind === 'segment' && addPointAt(hit.pi, hit.si, hit.at)} data-testid="edit-points-menu-add-point"><PlusCircle className="w-4 h-4 text-muted-foreground" /> Add Point</ContextMenuItem>
            <ContextMenuItem className="text-[13px] flex items-center gap-2" disabled={hit.kind !== 'node' || hitPath.nodes.length <= minNodes(hitPath)} onClick={() => hit.kind === 'node' && removeNode(hit.pi, hit.ni)} data-testid="edit-points-menu-delete-point"><Trash2 className="w-4 h-4 text-muted-foreground" /> Delete Point</ContextMenuItem>
            <ContextMenuSeparator />
            {hit.kind === 'node' ? (
              <>
                {[['corner', 'Corner Point', CornerDownRight], ['smooth', 'Smooth Point', Waves], ['straight', 'Straight Point', Minus]].map(([t, label, Icon]) => (
                  <ContextMenuItem key={t} className={`text-[13px] flex items-center gap-2 ${hitNode?.type === t ? 'bg-primary/10' : ''}`} onClick={() => apply(setNodeType(ff, hit.pi, hit.ni, t))} data-testid={`edit-points-menu-${t}`}><Icon className="w-4 h-4 text-muted-foreground" /> {label}{hitNode?.type === t && <Check className="w-3.5 h-3.5 ml-auto" />}</ContextMenuItem>
                ))}
              </>
            ) : (
              <>
                {hitSeg && isCurved(hitSeg[0], hitSeg[1])
                  ? <ContextMenuItem className="text-[13px] flex items-center gap-2" onClick={() => apply(setSegmentStraight(ff, hit.pi, hit.si))} data-testid="edit-points-menu-straight-segment"><Minus className="w-4 h-4 text-muted-foreground" /> Straight Segment</ContextMenuItem>
                  : <ContextMenuItem className="text-[13px] flex items-center gap-2" onClick={() => apply(setSegmentCurved(ff, hit.pi, hit.si))} data-testid="edit-points-menu-curved-segment"><Waves className="w-4 h-4 text-muted-foreground" /> Curved Segment</ContextMenuItem>}
              </>
            )}
            <ContextMenuSeparator />
            {hitPath.closed
              ? <ContextMenuItem className="text-[13px] flex items-center gap-2" onClick={() => apply(setPathClosed(ff, hit.pi, false))} data-testid="edit-points-menu-open-path"><Unlink className="w-4 h-4 text-muted-foreground" /> Open Path</ContextMenuItem>
              : <ContextMenuItem className="text-[13px] flex items-center gap-2" onClick={() => apply(setPathClosed(ff, hit.pi, true))} data-testid="edit-points-menu-close-path"><Link2 className="w-4 h-4 text-muted-foreground" /> Close Path</ContextMenuItem>}
            <ContextMenuSeparator />
            <ContextMenuItem className="text-[13px] flex items-center gap-2" onClick={exit} data-testid="edit-points-menu-exit"><LogOut className="w-4 h-4 text-muted-foreground" /> Exit Edit Points</ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

/** Floating mode banner (rendered outside the scrolling page so it is never clipped). */
export const EditPointsBanner = ({ object }) => {
  const { rawDispatch } = useEditor();
  const count = nodeCount(object.properties?.freeform);
  const exit = () => rawDispatch({ type: 'SET_POINT_EDIT', payload: null });
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 top-2 z-[60] flex items-center gap-2 rounded-full border border-border bg-popover/90 backdrop-blur-md px-3 py-1 shadow-md text-[12px] whitespace-nowrap max-w-[calc(100%-1rem)]"
      role="status"
      data-testid="edit-points-banner"
    >
      <Spline className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
      <span className="text-foreground font-medium">Editing points</span>
      <span className="text-muted-foreground hidden lg:inline" data-testid="edit-points-banner-hint">· drag a point or segment · Ctrl+click a segment to add · Ctrl+click a point to delete · right-click for point types</span>
      <span className="text-muted-foreground" data-testid="edit-points-count">{count} pts</span>
      <Button size="sm" className="h-6 px-2 text-[12px]" onMouseDown={(e) => e.preventDefault()} onClick={exit} data-testid="edit-points-done"><Check className="h-3.5 w-3.5 mr-1" /> Done</Button>
    </div>
  );
};

/**
 * Context-menu items for a right-click on the shape *body* while its points are being edited (the on-canvas overlay
 * only covers points and segments). `at` = pointer position in the shape's 100×100 viewBox space.
 */
export const EditPointsBodyMenuItems = ({ object, at }) => {
  const { dispatch, rawDispatch } = useEditor();
  const ff = object.properties?.freeform;
  const near = ff && at ? nearestSegment(ff, at) : null;
  const path = near ? ff.paths[near.pi] : ff?.paths?.[0];
  const pi = near ? near.pi : 0;
  const item = 'text-[13px] flex items-center gap-2 cursor-pointer';
  const write = (nextFf) => {
    const norm = normalizeFreeform(object, nextFf);
    const data = norm
      ? { x: norm.x, y: norm.y, width: norm.width, height: norm.height, properties: { ...object.properties, freeform: norm.freeform } }
      : { properties: { ...object.properties, freeform: nextFf } };
    dispatch({ type: 'PUSH_HISTORY' });
    rawDispatch({ type: 'UPDATE_OBJECT', payload: { id: object.id, data } });
  };
  if (!ff || !path) return null;
  return (
    <>
      <ContextMenuLabel className="text-[11px] text-muted-foreground">Edit Points · {nodeCount(ff)} pts</ContextMenuLabel>
      <ContextMenuItem className={item} disabled={!near} onClick={() => near && write(insertNode(ff, near.pi, near.si, near.t).ff)} data-testid="edit-points-body-add-point"><PlusCircle className="w-4 h-4 text-muted-foreground" /> Add Point</ContextMenuItem>
      <ContextMenuSeparator />
      {path.closed
        ? <ContextMenuItem className={item} onClick={() => write(setPathClosed(ff, pi, false))} data-testid="edit-points-body-open-path"><Unlink className="w-4 h-4 text-muted-foreground" /> Open Path</ContextMenuItem>
        : <ContextMenuItem className={item} onClick={() => write(setPathClosed(ff, pi, true))} data-testid="edit-points-body-close-path"><Link2 className="w-4 h-4 text-muted-foreground" /> Close Path</ContextMenuItem>}
      <ContextMenuSeparator />
      <ContextMenuItem className={item} onClick={() => rawDispatch({ type: 'SET_POINT_EDIT', payload: null })} data-testid="edit-points-body-exit"><LogOut className="w-4 h-4 text-muted-foreground" /> Exit Edit Points</ContextMenuItem>
    </>
  );
};
